import type { ECharts } from 'echarts';
import type { ChartExportOptions } from './types';

/**
 * Downloads a data URL as file
 * @param dataUrl - Data URL to download
 * @param filename - Name of the downloaded file
 * @example
 * downloadDataUrl('data:image/png;base64,...', 'chart.png')
 */
function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Exports ECharts instance to PNG image
 * @param chartInstance - ECharts instance
 * @param options - Export configuration
 * @example
 * const chartRef = useRef<ReactECharts>(null);
 * exportChartToPNG(
 *   chartRef.current?.getEchartsInstance(),
 *   { filename: 'dashboard-chart.png', pixelRatio: 2 }
 * )
 */
export function exportChartToPNG(chartInstance: ECharts | undefined | null, options?: ChartExportOptions): void {
  if (!chartInstance) {
    console.error('Chart instance is not available');
    return;
  }

  try {
    const dataUrl = chartInstance.getDataURL({
      type: 'png',
      pixelRatio: options?.pixelRatio || 2,
      backgroundColor: options?.backgroundColor || '#ffffff',
      excludeComponents: options?.excludeComponents
    });

    downloadDataUrl(dataUrl, options?.filename || 'chart.png');
  } catch (error) {
    console.error('Failed to export chart to PNG:', error);
    throw new Error('Failed to export chart to PNG');
  }
}

/**
 * Exports ECharts instance to SVG format
 * @param chartInstance - ECharts instance
 * @param options - Export configuration
 * @example
 * exportChartToSVG(chartInstance, { filename: 'chart.svg' })
 */
export function exportChartToSVG(chartInstance: ECharts | undefined | null, options?: ChartExportOptions): void {
  if (!chartInstance) {
    console.error('Chart instance is not available');
    return;
  }

  try {
    const svg = chartInstance.renderToSVGString();
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = options?.filename || 'chart.svg';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export chart to SVG:', error);
    throw new Error('Failed to export chart to SVG');
  }
}

/**
 * Extracts data from series with category-value objects
 * @example
 * extractCategoryValueData([
 *   { name: 'Series1', data: [{ category: 'Jan', value: 100 }] }
 * ])
 */
function extractCategoryValueData(series: Array<{ name?: string; data?: unknown }>): {
  headers: string[];
  rows: (string | number | null)[][];
} {
  const headers = ['Category'];
  const categoriesMap = new Map<string, (number | null)[]>();

  series.forEach((s, index) => {
    if (s.name) headers.push(s.name);

    const items = s.data as Array<{ category: string; value: number }> | undefined;
    items?.forEach((item) => {
      if (!categoriesMap.has(item.category)) {
        categoriesMap.set(item.category, new Array(series.length).fill(null));
      }
      categoriesMap.get(item.category)![index] = item.value;
    });
  });

  const rows = Array.from(categoriesMap.entries()).map(([category, values]) => [category, ...values]);

  return { headers, rows };
}

/**
 * Extracts data from pie chart series
 * @example
 * extractPieData([{ name: 'Pie', value: 100 }])
 */
function extractPieData(data: unknown[]): {
  headers: string[];
  rows: (string | number)[][];
} {
  const headers = ['Name', 'Value'];
  const rows = (data as Array<{ name?: string; value?: number }>)
    .filter((item) => item.name !== undefined && item.value !== undefined)
    .map((item) => [item.name!, item.value!]);

  return { headers, rows };
}

/**
 * Extracts data from standard line/bar charts
 * @example
 * extractStandardData(
 *   ['Mon', 'Tue'],
 *   [{ name: 'Sales', data: [100, 200] }]
 * )
 */
function extractStandardData(
  categories: string[],
  series: Array<{ name?: string; data?: unknown }>
): {
  headers: string[];
  rows: (string | number | null)[][];
} {
  const headers = ['Category'];
  series.forEach((s) => {
    if (s.name) headers.push(s.name);
  });

  const rows = categories.map((category, index) => {
    const row: (string | number | null)[] = [category];
    series.forEach((s) => {
      const values = s.data as number[] | undefined;
      row.push(values?.[index] ?? null);
    });
    return row;
  });

  return { headers, rows };
}

/**
 * Converts chart data to table format for export
 * @param chartOption - ECharts option object
 * @returns Headers and rows for table export
 * @example
 * extractChartData({
 *   xAxis: { data: ['Mon', 'Tue'] },
 *   series: [{ name: 'Sales', data: [100, 200] }]
 * })
 */
export function extractChartData(chartOption: Record<string, unknown>): {
  headers: string[];
  rows: (string | number | boolean | null)[][];
} {
  const series = chartOption.series as Array<{ name?: string; data?: unknown }> | undefined;

  if (!series?.length) {
    return { headers: [], rows: [] };
  }

  const firstData = series[0].data as unknown[] | undefined;
  if (!firstData?.length) {
    return { headers: [], rows: [] };
  }

  const firstItem = firstData[0];

  // Check data format
  if (typeof firstItem === 'object' && firstItem !== null) {
    if ('category' in firstItem && 'value' in firstItem) {
      return extractCategoryValueData(series);
    }
    if ('name' in firstItem && 'value' in firstItem) {
      return extractPieData(firstData);
    }
  }

  // Standard number array format
  const xAxis = chartOption.xAxis as { data?: string[] } | undefined;
  if (xAxis?.data) {
    return extractStandardData(xAxis.data, series);
  }

  return { headers: [], rows: [] };
}
